import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = {
    session,
    user: session?.user || null,
    loading,
    // True when an admin provisioned a temporary/invite password to be replaced.
    mustSetPassword: session?.user?.user_metadata?.must_set_password === true,
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signOut: () => supabase.auth.signOut(),
    resetPassword: (email) =>
      supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` }),
    updatePassword: (password) => supabase.auth.updateUser({ password }),
    // Change password with current-password verification (re-auth as the same user).
    updatePasswordSecure: async ({ currentPassword, newPassword }) => {
      const email = session?.user?.email;
      if (!email) return { error: { message: "Your session has expired. Please sign in again." } };
      const { error: reauthErr } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
      if (reauthErr) return { error: { message: "Your current password is incorrect." } };
      return supabase.auth.updateUser({ password: newPassword });
    },
    // Establish a permanent password and clear the temporary-password flag.
    establishPassword: (password) =>
      supabase.auth.updateUser({ password, data: { must_set_password: false } }),
    // Session controls (supports future "sign out other sessions" UI).
    signOutOtherSessions: () => supabase.auth.signOut({ scope: "others" }),
    signOutEverywhere: () => supabase.auth.signOut({ scope: "global" }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
